import { I18n } from '@lingui/core'
import { withI18n } from '@lingui/react'
import { type NowMerchantExtended } from '@redteclab/api/clients/bully'
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import logger from 'shared/services/logger'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type IState, type NowDataState } from 'types/redux'
import Modal from 'views/components/molecules/Modal'
import NowZipForm, {
  type INowDeliveryBoxData,
  type INowSlotExtended,
} from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowZipForm'
import { MINIMUM_VALUE_NOT_REACHED } from 'views/consts/notEligibleReasons'
import buildClientAPI from 'views/providers/clientAPI'

interface IProductNowShippingModal {
  id: string
  i18n: I18n
  open: boolean
  setModalOpen: Dispatch<SetStateAction<boolean>>
  upid: string
  price: number
  nowEligible: boolean
  setNowEligible: Dispatch<SetStateAction<boolean>>
}

const ProductNowShippingModal: (
  props: IProductNowShippingModal
) => ReactElement = ({
  open,
  id,
  i18n,
  setModalOpen,
  upid,
  price,
  nowEligible,
  setNowEligible,
}) => {
  const [notEligibleReasonCode, setNotEligibleReasonCode] = useState<
    number | undefined
  >(0)
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)
  const [zipCode, setZipCode] = useState(nowData?.zipCode)
  const [nowDeliveryBoxData, setNowDeliveryBoxData] =
    useState<INowDeliveryBoxData | null>(null)

  const dispatch = useDispatch()

  const handleSubmit = () => {
    dispatch(
      updateNowDataSuccessAction({
        ...nowData,
        zipCode,
      })
    )
  }

  const fetchNowData: (zipToUse: string) => Promise<void> = useCallback(
    async zipToUse => {
      try {
        if (zipToUse) {
          const { nowService } = buildClientAPI()

          const response = await nowService.fetchBasicData({
            zipCode: zipToUse,
            products: [
              {
                upid,
                quantity: 1,
                priceCentsGross: price * 100,
                nowProduct: 0,
              },
            ],
          })
          let notEligibleReason: number | undefined = response.notEligibleReason

          if (notEligibleReason === MINIMUM_VALUE_NOT_REACHED) {
            notEligibleReason = undefined
          }

          setNotEligibleReasonCode(notEligibleReason)
          const nowEligibleProduct: boolean =
            response.nowEligible ||
            response.notEligibleReason === MINIMUM_VALUE_NOT_REACHED
          setNowEligible(nowEligibleProduct)

          const slot = response?.slots?.length ? response.slots[0] : []
          setNowDeliveryBoxData({
            slot: { ...slot } as INowSlotExtended,
            merchant: { ...response.merchant } as NowMerchantExtended,
          })
          dispatch(
            updateNowDataSuccessAction({
              ...response,
              city: nowData?.city,
              zipCode: zipToUse,
              refreshNowData: false,
            })
          )
          setZipCode(zipToUse)
        } else {
          setNotEligibleReasonCode(undefined)
          setNowEligible(false)
          dispatch(updateNowDataSuccessAction({}))
        }
      } catch (error) {
        logger.error(error)
      }
    },
    [dispatch, nowData?.city, price, setNowEligible, upid]
  )

  const selectZip: () => void = useCallback(() => {
    let zipToUse = zipCode
    if (zipCode !== nowData?.zipCode) {
      zipToUse = nowData?.zipCode
    }

    void fetchNowData(zipToUse)
  }, [fetchNowData, nowData?.zipCode, zipCode])

  useEffect(() => {
    if (nowData?.refreshNowData) {
      selectZip()
    }
  }, [nowData?.refreshNowData, selectZip])

  useEffect(() => {
    if (nowData?.openZipLabelModal) {
      setModalOpen(true)
      dispatch(
        updateNowDataSuccessAction({
          ...nowData,
          openZipLabelModal: false,
        })
      )
    }
  }, [dispatch, nowData, setModalOpen])

  return (
    <>
      {open ? (
        <Modal
          id={id}
          title={i18n._('general.label.nowGlobalBar.chooseLocation')}
          size="medium"
          light
          open={open}
          isClientSideControlled
          onHideModal={() => {
            setModalOpen(false)
          }}
        >
          <NowZipForm
            nowEligible={nowEligible}
            zipCode={zipCode}
            setZipCode={setZipCode}
            handleSubmit={handleSubmit}
            nowDeliveryBoxData={nowDeliveryBoxData}
            notEligibleReasonCode={notEligibleReasonCode}
          />
        </Modal>
      ) : null}
    </>
  )
}

export default withI18n()(ProductNowShippingModal)
